import { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import styles from "./Sidebar.module.css";
import PersonIcon from "@mui/icons-material/Person";
import ScienceIcon from "@mui/icons-material/Science";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  handleButtonClickEventBuilder,
  sendEvent,
} from "../../utils/event-helpers";
import { useAppDispatch, useAppSelector } from "../../app/store";
import cx from "classnames";
import { BookmarkIcon, ChevronLeftIcon } from "lucide-react";
import type { MentorRecommendations } from "../../shared/recommendationTypes";
import useTenant from "../../tenancy/useTenantPath";

const Sidebar = ({
  tempRecommendations,
}: {
  tempRecommendations?: MentorRecommendations;
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const { userId, sessionId } = useAppSelector((state) => state.session);
  const recommendations = useAppSelector(
    (state) => state.mentorRecommendations.data,
  );
  const dispatch = useAppDispatch();
  const tenant = useTenant();

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (
        window.innerWidth <= 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(target) &&
        !target.closest(".bars")
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const menuItem = [
    {
      path: "/recommenderpage/get-research-experience",
      name: "Research Experience",
      icon: <ScienceIcon fontSize="large" />,
    },
    {
      path: "/recommenderpage/explore-careers",
      name: "Careers",
      icon: <WorkIcon sx={{ fontSize: 40 }} />,
    },
    {
      path: "/recommenderpage/find-graduate-schools",
      name: "Graduate Schools",
      icon: <SchoolIcon />,
    },
    {
      path: "/recommenderpage/talk-to-an-expert",
      name: "Talk to an Expert",
      icon: <PersonIcon />,
    },
    {
      path: "/recommenderpage/find-internships",
      name: "Internships",
      icon: <LightbulbIcon />,
    },
  ];

  return (
    <div
      ref={sidebarRef}
      className={cx(styles.sidebar, !isOpen ? styles.closed : "")}
      data-tour="sidebar-container"
    >
      <button
        type="button"
        className={styles.collapseButton}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
        aria-expanded={isOpen}
        data-tour="sidebar-toggle"
      >
        <ChevronLeftIcon className={styles.collapseIcon} />
      </button>

      <nav className={styles.menuItems} data-tour="sidebar-tabs">
        {menuItem.map((item) => {
          const displayRecommendations = tempRecommendations ?? recommendations;
          const topRecommendation =
            displayRecommendations?.recommendedPages.find(
              (page) => page.priority === 1,
            );

          const isBestRecommendedPage = topRecommendation
            ? item.path.includes(topRecommendation.pageRoute)
            : false;
          const isRecommendedPage =
            displayRecommendations?.recommendedPages.find((page) =>
              item.path.includes(page.pageRoute),
            );

          return (
            <NavLink
              onClick={() => {
                if (!userId || !sessionId) return;

                const eventData = handleButtonClickEventBuilder(
                  userId,
                  sessionId,
                  item.name,
                  {},
                );
                void dispatch(sendEvent(eventData));
              }}
              to={tenant.basePath + item.path}
              end={item.path === "/recommenderpage"}
              key={item.path}
              className={({ isActive }) =>
                cx(styles.menuItem, isActive ? styles.active : "")
              }
              data-cy="sidebar-link"
              data-path={item.path}
              data-tour={`sidebar-link-${item.name}`}
            >
              <span className={styles.iconWrapper}>
                <span className={styles.iconShell}>
                  {item.icon}

                  {isRecommendedPage && (
                    <span
                      className={styles.notificationDot}
                      aria-hidden="true"
                      data-tour="notification-dot"
                    />
                  )}

                  {!isOpen && (
                    <span className={styles.tooltip}>{item.name}</span>
                  )}
                </span>
                <span className={styles.itemText}>{item.name}</span>
              </span>

              {isOpen && isBestRecommendedPage && (
                <span className={styles.itemBadge} data-tour="best-match">
                  Best Match
                </span>
              )}
            </NavLink>
          );
        })}

        <div className={styles.menuDivider} />

        <NavLink
          to={tenant.basePath + "/recommenderpage/bookmarks"}
          className={({ isActive }) =>
            cx(styles.menuItem, isActive ? styles.active : "")
          }
          data-cy="sidebar-link"
          data-path={"/recommenderpage/bookmarks"}
          data-tour="sidebar-link-Bookmarks"
        >
          <div className={styles.iconWrapper}>
            <span className={styles.iconShell}>
              <BookmarkIcon />

              {!isOpen && <span className={styles.tooltip}>Bookmarks</span>}
            </span>

            <span className={styles.itemText}>Bookmarks</span>
          </div>
        </NavLink>
      </nav>
    </div>
  );
};

export default Sidebar;
